/**
 * BroadcastProvider - app-level singleton for the live broadcast session.
 *
 * The useBroadcastSession() hook owns status polling and a frame upload
 * pump, both backed by setInterval. If we let two siblings (the
 * BroadcastPill and the DexView banner, for example) each call the hook,
 * each would spawn its own pump and we would either double-upload every
 * frame or race the file consume + delete.
 *
 * The provider runs the hook exactly once, hangs the result on a context,
 * and every consumer reads from that context. This is the same pattern
 * SessionProvider / AuthProvider already use in this codebase.
 *
 * Mount in AppShell (or the root provider tree) so it lives for the app
 * lifetime; the hook itself is cheap when idle (no timers running until
 * the trainer taps Start).
 */
import { createContext, useContext, ReactNode } from "react";
import { useBroadcastSession, type BroadcastSessionStats } from "@/hooks/use-broadcast-session";

interface BroadcastContextValue {
  stats: BroadcastSessionStats;
  start: () => Promise<void>;
  cancel: () => Promise<void>;
  retry: () => void;
}

const BroadcastContext = createContext<BroadcastContextValue | null>(null);

export function BroadcastProvider({ children }: { children: ReactNode }) {
  const session = useBroadcastSession();
  return (
    <BroadcastContext.Provider value={session}>
      {children}
    </BroadcastContext.Provider>
  );
}

export function useBroadcast(): BroadcastContextValue {
  const ctx = useContext(BroadcastContext);
  if (!ctx) {
    throw new Error("useBroadcast must be used inside a BroadcastProvider");
  }
  return ctx;
}
